import React, { useState, useRef, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { useTSAccountProtection } from "@transmitsecurity/riskid-reactjs-ts";
import { TransitionProps } from "@mui/material/transitions";
import {
  Button,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  Tooltip,
} from "@mui/material";
import Preloader from "src/utils/Preloader";
import { LegalStyles } from "./legal.styles";
import { useHistory } from "react-router-dom";
import { logoutUser } from "src/utils/SessionManagement";
import api from "src/store/features/api";
import { trackEvent } from "src/Amplitude";
import TermsOfBusinessMarkdown from "./TermsOfBusinessMarkdown";
import usePermission from "src/utils/hooks/usePermission";
import { useUserSlice } from "src/store/features/user.slice";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function LegalDialog() {
  const { data: user, isSuccess, refetch } = api.useGetUserDatav2Query();
  const { acceptTerms } = isSuccess ? user : { acceptTerms: "pending" };
  const { accountCreateTime } = useUserSlice();
  const { userPermission } = usePermission();
  const classes = LegalStyles();
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [warningModal, setWarningModal] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const boxInnerRef = useRef();
  const warningMessage =
    "By rejecting our Terms of Business we are unable to offer you access to C&B Portal. Do you wish to accept Terms of Business?";
  const history = useHistory();
  const theme = useTheme();
  const { clearUser } = useTSAccountProtection();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [acceptTermsQuery] = api.useAcceptTermsMutation();

  const handleClick = async (userSelection: string) => {
    const dateTime = new Date().toString();
    switch (userSelection) {
      case "Accept":
        await acceptTermsQuery();
        trackEvent("Terms and Conditions", { option: "Accept", dateTime }, "Legal Dialog");
        setOpen(false);
        setWarningModal(false);
        refetch(); // refetch new user data
        break;
      case "Legal Declined":
        setWarningModal(true);
        break;
      case "Warning Declined":
        trackEvent("Terms and Conditions", { option: "Decline", dateTime }, "Legal Dialog");
        // clears the user context for all subsequent events in the browser session
        await clearUser();
        logoutUser();
        history.push("/");
        break;
    }
  };

  // today in epoch time
  const today = new Date().getTime();

  // is account create time less than 8 months?
  const isPortalSignUp = accountCreateTime && today - accountCreateTime < 8.64e7 * 30 * 8;

  // Disabling ToB request from Onboarding 16/11/2023
  useEffect(() => {
    // Adding conditions to only display to users that are not onboarded through portal
    if (acceptTerms !== "pending" && userPermission !== "guest" && !isPortalSignUp) {
      // new account, acceptTerms is missing
      if (typeof acceptTerms === "undefined" || !acceptTerms) {
        setOpen(true);
      }
      // user has already accepted terms and conditions
      if (acceptTerms) setOpen(false);
    }
  }, [acceptTerms, isPortalSignUp, userPermission]);

  const WarningDialog = () => {
    return (
      <>
        {warningModal && (
          <Dialog
            sx={{ backgroundColor: "transparent" }}
            open={warningModal}
            TransitionComponent={Transition}
            style={{ zIndex: 21474830032 }}>
            <Container className={classes.container}>
              <Typography className={classes.modalMessage}>{warningMessage}</Typography>
              <ButtonGroup actionType={"Warning Declined"} />
            </Container>
          </Dialog>
        )}
      </>
    );
  };

  const ButtonGroup = (props: { actionType: string }) => {
    const { actionType } = props;
    return (
      <Tooltip
        title="Please finish reading the Terms and Conditions"
        arrow
        disableHoverListener={showButtons}
        classes={{ popper: classes.toolTip }}>
        <Grid
          sx={{ height: "100%", marginBottom: "0", marginTop: "0" }}
          container
          spacing={2}
          mb={4}>
          <Grid
            item
            xs={6}
            sx={{
              display: "flex",
              alignItems: "center",
              paddingTop: "0 !important",
            }}
            pt={0}>
            <Button
              disableRipple
              fullWidth
              variant={"outlined"}
              onClick={() => handleClick(actionType)}
              disabled={!showButtons}>
              <Typography style={{ fontWeight: 600 }}>Decline</Typography>
            </Button>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{
              display: "flex",
              alignItems: "center",
              paddingTop: "0 !important",
            }}
            pt={0}>
            <Button
              disableRipple
              fullWidth
              variant={"contained"}
              onClick={() => {
                trackEvent("Accepted terms of business", { option: "Accept" }, "Legal Dialog");
                handleClick("Accept");
              }}
              disabled={!showButtons}>
              <Typography style={{ fontWeight: 600 }}>Accept</Typography>
            </Button>
          </Grid>
        </Grid>
      </Tooltip>
    );
  };

  // reveal buttons when user has read the T&C
  const onScroll = () => {
    if (boxInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = boxInnerRef.current;
      if (scrollTop + clientHeight + 500 > scrollHeight) {
        setShowButtons(true);
      }
    }
  };

  return (
    <>
      {/* Legal Modal */}
      <Dialog
        sx={{
          backgroundColor: "transparent",
          zIndex: "21474830031 !important",
          overflow: "auto",
        }}
        open={open}
        TransitionComponent={Transition}
        maxWidth={false}
        fullScreen={isMobile}>
        <Grid item container className={classes.legalModalGrid}>
          <Typography component="h1" variant="h1" className={classes.modalTitle}>
            Caleb and Brown Updated Terms of Business
          </Typography>
          <Box
            className={classes.scollBar}
            ref={boxInnerRef}
            onScroll={!showButtons ? onScroll : () => null}>
            <TermsOfBusinessMarkdown setReady={setReady} />
          </Box>
          <Box className={classes.buttonWrapper}>
            {ready && <ButtonGroup actionType={"Legal Declined"} />}
            {!ready && <Preloader />}
          </Box>
        </Grid>

        {/* Wait until markdown fetch md */}
      </Dialog>
      {/* Warning Modal */}
      <WarningDialog />
    </>
  );
}
